import { Text } from '@mantine/core';
import { match } from 'ts-pattern';
import { IconBug, IconGolfFilled, IconRocket } from '@tabler/icons-react';
import type { AIMessage } from '~/common/vm/protocol';
import { CollapsibleCard } from '../CollapsibleCard';
import { api } from '~/trpc-extra/api';

export function AIGenerationStatusCard({ message }: { message: Extract<AIMessage, { type: 'ai-generation-status' }> }) {
  const { mutateAsync: getLangSmithAiStepTraceUrl } = api.admin.getLangSmithAiTraceUrl.useMutation();

  const title = match(message.generationReason)
    .with('step', () =>
      message.status === 'begin' ? 'Generating draft test actions' : 'Successfully generated and refined actions',
    )
    .with('action', () => (message.status === 'begin' ? 'Performing action' : 'Successfully performed action'))
    .with('autoheal', () => (message.status === 'begin' ? 'Started autoheal' : 'Successfully healed the step'))
    .exhaustive();

  const config = match(message.status)
    .with('begin', () => ({
      icon: IconRocket,
      iconColor: 'text-gray-500 dark:text-gray-400',
    }))
    .with('finished', () => ({
      icon: IconGolfFilled,
      iconColor: 'text-gray-500 dark:text-gray-400',
    }))
    .exhaustive();

  return (
    <>
      {message.status === 'begin' && (
        <Text
          size="xs"
          className="mb-2 text-center text-gray-400 dark:text-gray-500"
        >
          AI {message.generationReason} started at {new Date(message.epochMs).toLocaleTimeString()}
        </Text>
      )}
      <CollapsibleCard
        icon={config.icon}
        title={title}
        defaultExpanded={true}
        classNames={{
          icon: config.iconColor,
        }}
        headerButtons={[
          {
            name: `Debug AI ${message.generationReason} in LangSmith`,
            icon: IconBug,
            adminOnly: true,
            callback: async () => {
              try {
                const traceUrl = await getLangSmithAiStepTraceUrl({
                  executionId: message.executionId,
                  stepId: message.stepId,
                  initiator: message.initiator,
                  operationId: message.operationId,
                });
                window.open(traceUrl, '_blank');
              } catch (error) {
                console.error(`Failed to get LangSmith AI ${message.generationReason} trace URL:`, error);
              }
            },
          },
        ]}
      >
        <div className="space-y-2">
          <div className="rounded border border-gray-200 bg-gray-50 px-3 dark:border-gray-700 dark:bg-gray-800">
            <Text
              size="sm"
              className="italic leading-relaxed"
            >
              &quot;{message.prompt}&quot;
            </Text>
          </div>
        </div>
      </CollapsibleCard>
      {message.status === 'finished' && (
        <Text
          size="xs"
          className="mt-2 text-center text-gray-400 dark:text-gray-500"
        >
          AI {message.generationReason} finished at {new Date(message.epochMs).toLocaleTimeString()}
        </Text>
      )}
    </>
  );
}
